/**
 * Utilitaires partagés autour des fichiers d'élèves.
 * Consommés par le wizard d'import établissement (importEtablissement.js,
 * EtapeFichier) et par l'import d'élèves dans les groupes (ImportElevesGroupe).
 *
 * (Les anciens helpers de l'import par classe — parseClipboardContent,
 * groupElevesByClasse, categorizeClasses, findNewStudents... — ont été purgés
 * avec add_multiple : le moteur /eleves/reconcilier/ fait ce travail au back.)
 */

/**
 * Corrige les problèmes d'encodage des caractères accentués
 */
export const fixEncoding = (str) => {
    if (!str || typeof str !== 'string') return str || '';
    return str
        .replace(/Ã©/g, 'é')
        .replace(/Ã¨/g, 'è')
        .replace(/Ã¢/g, 'â')
        .replace(/Ã«/g, 'ë')
        .replace(/Ãª/g, 'ê')
        .replace(/Ã¹/g, 'ù')
        .replace(/Ã"/g, 'Ô')
        .replace(/Ã‰/g, 'É')
        .replace(/Ã/g, 'à');
};

/**
 * Détecte automatiquement le séparateur CSV
 */
export const detectSeparator = (line) => {
    if (line.includes('\t')) return '\t';
    if (line.includes(',')) return ',';
    if (line.includes(';')) return ';';
    return null;
};

/**
 * Parse une ligne CSV/TSV en gérant les guillemets
 */
export const parseCSVLine = (line, separator) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (char === '"') {
            inQuotes = !inQuotes;
        } else if (char === separator && !inQuotes) {
            result.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }
    result.push(current.trim());
    return result;
};

/**
 * Vérifie si le format de fichier est supporté
 */
export const isFileFormatSupported = (fileName) => {
    const supportedFormats = ['.csv', '.xlsx', '.xls', '.ods'];
    return supportedFormats.some(format => fileName.toLowerCase().endsWith(format));
};

/**
 * Normalise un nom de classe pour comparaison tolérante :
 * - minuscules
 * - suppression des accents (NFD + diacritiques)
 * - suppression des espaces, tirets, underscores
 * Exemple : "501 LCE-ULIS" → "501lceulis"
 */
// Diacritiques combinants U+0300–U+036F (regex construite par char codes pour
// éviter tout souci d'échappement d'éditeur)
const DIACRITIQUES = new RegExp('[' + String.fromCharCode(0x300) + '-' + String.fromCharCode(0x36f) + ']', 'g');

const normalizeClasseName = (name) => {
    if (!name) return '';
    return name
        .toLowerCase()
        .normalize("NFD").replace(DIACRITIQUES, "")
        .replace(/[\s\-_]/g, "")
        .trim();
};

/**
 * Recherche intelligente d'une classe TanoEd à partir du nom Excel.
 * 1) Match exact après normalisation (accents, espaces, casse ignorés)
 * 2) Fallback startsWith : gère la troncature Excel
 *    ex: fichier "501" → TanoEd "501 LCE ULIS" → match si candidat unique
 *    Si plusieurs candidats (ex: "501A" et "501B" pour "501") → null (ambigu)
 *
 * @param {string} classeKey - Nom de classe du fichier (UPPERCASE ou non)
 * @param {Array} listClasses - Liste des classes TanoEd [{id, nom, nombre_eleves, ...}]
 * @returns {Object|null} - La classe TanoEd matchée, ou null si non trouvée / ambiguë
 */
export const findClasseMatch = (classeKey, listClasses) => {
    if (!classeKey || !listClasses?.length) return null;

    const keyNormalized = normalizeClasseName(classeKey);

    // 1) Match exact normalisé
    const exactMatch = listClasses.find(c =>
        normalizeClasseName(c.nom) === keyNormalized
    );
    if (exactMatch) return exactMatch;

    // 2) startsWith : le nom TanoEd commence par le nom fichier (troncature Excel)
    //    Ex: "501" → "501lceulis".startsWith("501") = true
    const candidates = listClasses.filter(c =>
        normalizeClasseName(c.nom).startsWith(keyNormalized)
    );
    // Un seul candidat → match non-ambigu
    if (candidates.length === 1) return candidates[0];

    // 0 ou plusieurs candidats → non reconnu
    return null;
};
